import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import dynamicHydrate from '../../dynamic-hydration/dynamic-hydrate'
import { useStoreLanguages } from '../../hooks/store-languages/useStoreLanguages'
import { useCountryShippingDefault } from '../../hooks/useCountryShippingDefault'
import { useSwitchStoreUrl } from '../../hooks/useSwitchStoreUrl'
import { useWorldStores } from '../../hooks/world-stores/useWorldStores'
import { useConfig } from '../config'
import { useStore } from '../store'

const ChangeCountryLanguageContext = createContext()

const ChangeCountryLanguageDropdown = dynamicHydrate(
	() => import('@bluheadless/ui/src/organisms/change-country-language/change-country-language-dropdown'),
	{ on: ['idle'] }
)

const ChangeCountryLanguageProvider = ({ children }) => {
	const [open, setOpen] = useState(false)
	const { locale } = useConfig()
	const { country: defaultShippingCountry } = useCountryShippingDefault()

	const { switchStoreUrl } = useSwitchStoreUrl()
	const openChangeCountryLanguageDropdown = useCallback(() => {
		setOpen(true)
	}, [])

	const closeChangeCountryLanguageDropdown = useCallback(() => {
		setOpen(false)
	}, [])

	const onUpdateChangeCountryLanguage = useCallback(
		(e, { store, country }) => {
			return switchStoreUrl(store, country)
		},
		[switchStoreUrl]
	)

	const contextValue = useMemo(
		() => [
			{}, // value
			{
				openChangeCountryLanguageDropdown,
				closeChangeCountryLanguageDropdown,
			}, // api
		],
		[openChangeCountryLanguageDropdown, closeChangeCountryLanguageDropdown]
	)

	const { code: currentStoreCode } = useStore()
	const { languages } = useStoreLanguages({ store: currentStoreCode })
	const { isSingleWebsite } = useWorldStores()
	const isSingleLanguage = languages?.length === 1

	return (
		<ChangeCountryLanguageContext.Provider value={contextValue}>
			{children}
			{(!isSingleWebsite || !isSingleLanguage) && (
				<ChangeCountryLanguageDropdown
					defaultCountryCode={defaultShippingCountry?.code}
					defaultLocale={locale}
					countryCode={defaultShippingCountry?.code}
					locale={locale}
					onClose={closeChangeCountryLanguageDropdown}
					onUpdate={onUpdateChangeCountryLanguage}
					open={open}
					forceHydration={open}
				/>
			)}
		</ChangeCountryLanguageContext.Provider>
	)
}

export default ChangeCountryLanguageProvider
export const useChangeCountryLanguage = () => useContext(ChangeCountryLanguageContext)
